import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { IconCheck, IconX, IconAlertTriangle, IconPackage, IconMessage } from '@tabler/icons-react';
import { TotemLightBox } from './TotemLightBox';
import { ControlPhotoFragmentFragment } from 'data/__generated__';
import { PhotoIssuePopup } from 'components/PhotoIssuePopup';
import { usePhotoActions } from 'hooks/usePhotoActions';
import { getTranslatedPhotoType } from 'helpers/photoTypeTranslation';

export function TotemGallery({ images }: { images: ControlPhotoFragmentFragment[] }) {
    const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);
    const [initialImageIndex, setInitialImageIndex] = useState(0);

    const {
        createInstructionActions,
        createDeliveryActions,
        createTaskInstructionActions,
        popupState,
        handleIssueTypeSelection,
        closeIssuePopup,
    } = usePhotoActions((url: string) => {
        const image = images.find((i) => i.url === url);
        return image
            ? {
                  type: image.type,
                  photoIndex: image.photoIndex,
                  state: image.state,
                  isMandatory: image.isMandatory,
              }
            : undefined;
    });

    const getTypeLabel = (photo: ControlPhotoFragmentFragment) =>
        photo.photoIndex === 0
            ? getTranslatedPhotoType(photo.type)
            : `${getTranslatedPhotoType(photo.type)} ${photo.photoIndex}`;

    const lightboxImages = useMemo(
        () =>
            images.map((photo) => ({
                url: photo.url,
                label: photo.microstoreColumn?.name
                    ? `${photo.microstoreColumn.name} - ${getTypeLabel(photo)}`
                    : getTypeLabel(photo),
            })),
        [images],
    );

    return (
        <>
            <Container>
                {images.map((photo, index) => {
                    const {
                        url,
                        state,
                        issueType,
                        comment,
                        setupInstructionId,
                        deliveryId,
                        taskInstructionId,
                        microstoreColumn,
                    } = photo;
                    const typeLabel = getTypeLabel(photo);

                    const actions = setupInstructionId
                        ? createInstructionActions(setupInstructionId)
                        : taskInstructionId
                          ? createTaskInstructionActions(taskInstructionId)
                          : deliveryId
                            ? createDeliveryActions(deliveryId)
                            : null;

                    const onValidate = actions?.onValidate;
                    const onRefuse = actions?.onRefuse;

                    return (
                        <ImageContainer key={index}>
                            <ImageTypeLabel>
                                {microstoreColumn?.name && (
                                    <MachineName title={microstoreColumn.name}>{microstoreColumn.name}</MachineName>
                                )}
                                <TypeLabel title={typeLabel}>{typeLabel}</TypeLabel>
                            </ImageTypeLabel>
                            <ImageWrapper>
                                {(issueType?.isBadQuality || issueType?.isBadQuantity || comment) && (
                                    <IssueTypeBadges>
                                        {issueType?.isBadQuality ? (
                                            <Badge data-label="Mauvaise qualité">
                                                <IconAlertTriangle size={14} />
                                            </Badge>
                                        ) : null}
                                        {issueType?.isBadQuantity ? (
                                            <Badge data-label="Mauvaise quantité">
                                                <IconPackage size={14} />
                                            </Badge>
                                        ) : null}
                                        {comment ? (
                                            <Badge data-label={comment}>
                                                <IconMessage size={14} />
                                            </Badge>
                                        ) : null}
                                    </IssueTypeBadges>
                                )}
                                <Image
                                    src={url}
                                    onClick={() => {
                                        setInitialImageIndex(index);
                                        setIsLightBoxOpen(true);
                                    }}
                                />
                                {(onValidate || onRefuse) && (
                                    <ActionButtons>
                                        {onValidate && (
                                            <ActionButton
                                                type="button"
                                                $isActive={state === 'Validated'}
                                                $type="validate"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onValidate(url);
                                                }}
                                            >
                                                <IconCheck size={16} />
                                            </ActionButton>
                                        )}
                                        {onRefuse && (
                                            <ActionButton
                                                type="button"
                                                $isActive={state === 'Refused'}
                                                $type="refuse"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRefuse(url);
                                                }}
                                            >
                                                <IconX size={16} />
                                            </ActionButton>
                                        )}
                                    </ActionButtons>
                                )}
                            </ImageWrapper>
                        </ImageContainer>
                    );
                })}
                {isLightBoxOpen &&
                    createPortal(
                        <LightboxLayer>
                            <TotemLightBox
                                images={lightboxImages}
                                isOpen
                                setIsOpen={setIsLightBoxOpen}
                                initialImageIndex={initialImageIndex}
                            />
                        </LightboxLayer>,
                        document.body,
                    )}
            </Container>
            <PhotoIssuePopup
                isOpen={popupState.isOpen}
                onClose={closeIssuePopup}
                onConfirm={handleIssueTypeSelection}
            />
        </>
    );
}

// Above the page headers and sticky table cells, which a lightbox drawn in place stays under.
const LightboxLayer = styled.div`
    position: relative;
    z-index: 1000;
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
`;

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 100px;
    max-width: 10vw;
    margin: 2px;
`;

const ImageTypeLabel = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 2px 4px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    border: 1px solid rgba(0, 0, 0, 0.3);
    border-radius: 12px 12px 0 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
`;

const MachineName = styled.div`
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TypeLabel = styled.div`
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ImageWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
    max-height: 40vh;
    overflow: visible;
`;

const Image = styled.img`
    cursor: pointer;
    object-fit: contain;
    width: 100%;
    height: 100%;
`;

const ActionButtons = styled.div`
    position: absolute;
    bottom: 0px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    padding: 0px;
`;

const ActionButton = styled.button<{ $isActive: boolean; $type: 'validate' | 'refuse' }>`
    width: 32px;
    height: 32px;
    border: none;
    background: ${({ theme, $type, $isActive }) =>
        $isActive ? ($type === 'validate' ? theme.successColor : theme.errorColor) : theme.cardBackgroundColor};
    color: ${({ theme, $isActive }) => ($isActive ? theme.ctaPrimaryTextColor : theme.textColor)};
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background 300ms ease,
        color 300ms ease,
        filter 300ms ease;

    &:hover {
        ${({ $isActive, theme, $type }) =>
            $isActive
                ? ''
                : `
                      background: ${$type === 'validate' ? theme.successColor : theme.errorColor};
                      color: ${theme.ctaPrimaryTextColor};
                      filter: brightness(0.85);
                  `}
    }
`;

const IssueTypeBadges = styled.div`
    position: absolute;
    top: 4px;
    left: 4px;
    display: flex;
    gap: 4px;
`;

const Badge = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};

    &:hover::after {
        content: attr(data-label);
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: ${({ theme }) => theme.cardBackgroundColor};
        border: 1px solid ${({ theme }) => theme.lightBorderColor};
        color: ${({ theme }) => theme.textColor};
        padding: 4px 6px;
        border-radius: 4px;
        margin-top: 4px;
        z-index: 20;
        font-size: 12px;
        line-height: 1;
        white-space: normal;
        word-break: break-word;
        max-width: 300px;
        min-width: 120px;
        text-align: left;
    }
`;
